import { useParams } from 'react-router-dom'
import { BoxPage } from '@/components/common'
import { Card, Typography, IconButton, Divider } from '@mui/material'
import { Button } from '@/@core/components/mui/Button'
import { useTranslator } from '@/hooks'
import { ActivityLogTypeEnum, Attendances, IUser, Transaction, TransactionStatusEnum, UserStatusEnum } from '@/@data'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import CustomAvatar from '@/@core/components/mui/Avatar'
import { getInitials } from '@/utils/getInitials'
import TransactionStatusChip from './TransactionStatusChip'
import { _ } from 'numeral'
import UserStatusChip from '../user/employee/UserStatusChip'
import { useNotification } from '@/hooks/useNotification'
import { useState, useMemo } from 'react'
import TransactionEmployee from '@/components/user/modal/TransactionEmployee'
import AppLogs from '../common/AppLogs'

const TransactionEmployeeDetail = () => {
  const { id } = useParams<{ id: string }>()
  const n = useNotification()
  const [isOpenLogs, setIsOpenLogs] = useState(false)
  const [openAttendanceModal, setOpenAttendanceModal] = useState(false)

  const getAvatar = (params: Pick<IUser, 'avatar_file' | 'first_name'>) => {
    const { avatar_file, first_name } = params

    if (avatar_file) {
      return <CustomAvatar src={avatar_file.tiny_url} skin='light' size={80} />
    } else {
      return (
        <CustomAvatar skin='light' size={80}>
          {getInitials(first_name as string)}
        </CustomAvatar>
      )
    }
  }

  const formatBankAccountNumber = (value?: string) => {
    if (!value) return ''

    const digitsOnly = value.replace(/\D/g, '')

    return digitsOnly.replace(/(.{4})/g, '$1 ').trim()
  }

  const { employeeId } = useParams<{ employeeId: string }>()
  const { __, __cc, __n } = useTranslator()

  const { data } = useQuery({
    ...Transaction.getTransactionsDetailById(employeeId),
    enabled: !!employeeId
  })

  const paramsCompany = useMemo(() => {
    return {
      companyId: data?.company_id,
      month: data?.month
    }
  }, [data?.company_id, data?.month])

  const { data: dataCompany } = useQuery({
    ...Attendances.getAttendanceDetailByCompanyId(paramsCompany),
    enabled: !!data?.company_id && !!data?.month && !!data
  })

  // employee
  const paramsEmployee = {
    companyId:data?.company_id,
    userId: data?.user_id,
    month: data?.month
  }

  const { data: dataEmployee } = useQuery({
    ...Attendances.getAttendanceDetailByCompanyIdEmployee(paramsEmployee),
    enabled: !!data?.company_id && !!data?.month && !!data?.user_id
  })

  return (
    <BoxPage
      showBackButton={true}
      title={__('Transaction Employee Detail')}
      breadcrumbs={[
        { label: __('Finance Management'), href: '/cms/finance/transaction' },
        { label: __('Transaction Management'), href: '/cms/finance/transaction' },
        { label: __('Transaction Employer Detail'), href: `/cms/finance/transaction/${id}` },
        { label: __('Transaction Employee Detail') }
      ]}
    >
      <Card sx={{ borderRadius: 3, backgroundColor: '#f8f9fa' }} className='p-6'>
        <div>
          <div className='flex items-center justify-between pb-4 mt-4 border-b'>
            <div className='flex items-center gap-4'>
              <Typography variant='h4' className='font-medium'>
                {data?.code}
              </Typography>
              <TransactionStatusChip status={data?.status as TransactionStatusEnum} noteFailed={data?.reason} />
            </div>
            <div className='flex items-center text-[15px] gap-6 text-sm text-gray-600'>
              <div className='flex items-center gap-2 font-normal'>
                <span>{__('Create Date')} : </span>
                { data?.created_at ? dayjs(data?.created_at).format('DD/MM/YYYY') : '-'}
              </div>
              <div className='flex items-center gap-2 font-normal'>
                <span>{__('Update Date')} : </span>
                {dayjs(data?.updated_at).format('DD/MM/YYYY')}
                {/* <Tooltip title='History'>
                  <IconButton
                    size='small'
                    onClick={() => {
                      setIsOpenLogs(true)
                    }}
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: '#2E263D14',
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                  >
                    <i className='tabler-history' style={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip> */}
              </div>
            </div>
          </div>
        </div>


        <div className='flex gap-6 py-6'>
          <div
            className={`flex-1 border-[#2F2B3D1F] border flex justify-between w-4/5 px-6 py-8 rounded-4xl
              ${
                data?.user?.status === UserStatusEnum.DELETED ||
                data?.user?.status === UserStatusEnum.WAITING_DELETE 
                ? 'bg-red-100' : 'bg-[#E9F9EF]'}
            `}
            style={{
              borderRadius: 16,
              backgroundColor: '#E9F9EF'
            }}
          >
            <div className='flex gap-4 mb-4'>
              <div className=''>
                <div className='flex items-center gap-8'>
                  <div>
                    {data?.user?.status === UserStatusEnum.DELETED
                      ? getAvatar({
                          // @ts-expect-error -- avatar_file is required, but none for deleted
                          avatar_file: undefined,
                          first_name: __('Unknown')
                        })
                      : getAvatar({
                          avatar_file: data?.user?.avatar_file,
                          first_name: data?.user?.first_name || '-'
                        })
                    }
                  </div>

                  <div className=''>
                    <div
                      className='flex gap-2 justify-between items-center'
                      style={{
                        lineHeight: '42px'
                      }}
                    >
                      <div className='text-[28px] font-medium'>{data?.user?.first_name || '-'}</div>
                      <div>
                        <UserStatusChip 
                          status={data?.user?.status as UserStatusEnum} 
                        />
                      </div>
                    </div>
                    <div className='flex items-center gap-2'>
                      <div className='flex items-center gap-1'>
                        <i className='tabler-id text-[18px]' />
                        {data?.user?.status !== UserStatusEnum.DELETED ? (
                          <span className='text-[15px]'>{data?.user?.system_code || '-'}</span>
                        ) : (
                          <span className='text-[15px]'>{__('Unknown')}</span>
                        )}

                        {data?.user?.status !== UserStatusEnum.DELETED && (
                          <IconButton
                            size='small'
                            onClick={() => {
                              navigator.clipboard.writeText(data?.user?.system_code || '')
                              n.success(__('Copied to clipboard!'))
                            }}
                          >
                            <i className='tabler-copy text-[18px]' />
                          </IconButton>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {data?.user?.status !== UserStatusEnum.DELETED && (
              <div>
                <Button
                  variant='tonal'
                  color='primary'
                  startIcon={<i className='tabler-calendar text-[16px] text-white' />}
                  onClick={() => setOpenAttendanceModal(true)}
                  className='bg-[#6f83f6]'
                >
                  <span className='text-[14px] text-white'>{__('Attendance Management')}</span>
                </Button>
              </div>
            )}
          </div>

          <div className={`w-[307px] p-6 text-white rounded-4xl bg-[url(/images/bank/bank.png)] bg-cover bg-center bg-no-repeat
            ${ 
              data?.user?.status === UserStatusEnum.DELETED ? 'bg-[url(/images/bank/bankdeleted.png)] bg-[#8D8D8D]' : 'bg-[url(/images/bank/banknew.png)] bg-[#3D5AFE]' 
            }
          `}>
            <div>
              <Typography variant='h5' className='text-white'>
                {data?.user?.status !== UserStatusEnum.DELETED ? data?.bank?.shortName : __('Unknown')}
              </Typography>
              <Typography variant='h5' className='my-6 text-white'>
                {data?.user?.status !== UserStatusEnum.DELETED ? data?.bank_account_name : __('Unknown')}
              </Typography>
              <Typography className='font-sans text-[20px] font-bold font-mono text-white'>
                {data?.user?.status !== UserStatusEnum.DELETED ? formatBankAccountNumber(data?.bank_account_number as any) : __('Unknown')}
              </Typography>
            </div>
          </div>
        </div>

        <div className='flex justify-between'>
          <div className='w-3/4 pr-8'>
            <Typography className='text-[24px] text-[000] font-medium mb-2 text-[#2f2b3de6]'>{__('Transaction Detail')}</Typography>
            <Typography className='text-gray-600 mb-6 text-[#2f2b3db3]'>
              {__('Review your transaction details - Confirm to complete payment securely.')}
            </Typography>

            <div className='mb-6'>
              <div className='overflow-x-auto'>
                <table className='w-full table-fixed border-gray-200 border rounded-xl'>
                  <colgroup>
                    <col style={{ width: 90 }} />
                    <col style={{ width: '40%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '35%' }} />
                  </colgroup>
                  <thead>
                    <tr className='bg-gray-50'>
                      <th className='px-4 py-2 text-left text-[#2f2b3de6] font-medium'>#</th>
                      <th className='px-4 py-2 text-left'>
                        <Typography className='text-[#2f2b3de6] font-medium'>{__('BALANCE')}</Typography>
                      </th>
                      <th className='px-4 py-2 text-left'>
                        <Typography className='text-[#2f2b3de6] font-medium'>{__('ELIGIBILITY RATE')}</Typography>
                      </th>
                      <th className='px-4 py-2 text-left'>
                        <Typography className='text-[#2f2b3de6] font-medium'>{__('ELIGIBLE AMOUNT')}</Typography>
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td className='border-t-[1px] px-4 py-2 text-[#2f2b3de6] text-[15px]'>1</td>
                      <td className='border-t-[1px] px-4 py-2 text-[#2f2b3de6] text-[13px]'>
                        <Typography>
                          {__n(Math.round((Number(data?.eligibility_amount) * 100) / Number(data?.eligibility_rate)))}{' '}
                          ₫
                        </Typography>
                      </td>
                      <td className='border-t-[1px] px-4 py-2 text-[#2f2b3de6] text-[13px]'>{data?.eligibility_rate} %</td>
                      <td className='border-t-[1px] px-4 py-2 text-[#2f2b3de6] text-[15px]'>{__cc(data?.eligibility_amount)} ₫</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className='overflow-x-auto'>
            <table className='w-full table-fixed border border-gray-200 rounded-xl'>
              <colgroup>
                <col style={{ width: 90 }} />
                <col style={{ width: '40%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '35%' }} />
              </colgroup>
              <thead>
                <tr className='bg-gray-50'>
                  <th className='px-4 py-2 text-left text-[#2f2b3de6] font-medium'>#</th>
                  <th className='px-4 py-2 text-left'>
                    <Typography className='text-[#2f2b3de6] font-medium'>{__('TYPE')}</Typography>
                  </th>
                  <th className='px-4 py-2 text-left'></th>
                  <th className='px-4 py-2 text-left'>
                    <Typography className='text-[#2f2b3de6] font-medium'>{__('AMOUNT')}</Typography>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className='border-t-[1px] px-4 py-2 text-[#2f2b3de6] text-[15px]'>2</td>
                  <td className='border-t-[1px] px-4 py-2 text-[#2f2b3de6] text-[15px]'>{__('Request Amount')}</td>
                  <td className='border-t-[1px] px-4 py-2 text-[#2f2b3de6] text-[13px]'></td>
                  <td className='border-t-[1px] px-4 py-2 text-[#2f2b3de6] text-[15px]'>{__cc(data?.request_amount)} ₫</td>
                </tr>

                <tr>
                  <td className='border-t-[1px] px-4 py-2 text-[#2f2b3de6] text-[15px]'>3</td>
                  <td className='border-t-[1px] px-4 py-2 text-[#2f2b3de6] text-[15px]'>{__('Service Fee')}</td>
                  <td className='border-t-[1px] px-4 py-2 text-[#2f2b3de6] text-[13px]'></td>
                  <td className='border-t-[1px] px-4 py-2 text-[#2f2b3de6] text-[135x]'>{__cc(data?.service_fee)} ₫</td>
                </tr>

                <tr>
                  <td className='border-t-[1px] px-4 py-2 text-[#2f2b3de6] text-[15px]'>4</td>
                  <td className='border-t-[1px] px-4 py-2 text-[#2f2b3de6] text-[15px]'>{__('Voucher')}</td>
                  <td className='border-t-[1px] px-4 py-2'></td>
                  <td className='border-t-[1px] px-4 py-2 text-[#2f2b3de6] text-[15px]'>{__cc(data?.voucher_discount)} ₫</td>
                </tr>
              </tbody>
            </table>
          </div>

          </div>

          <div className='w-1/4 border-l-[1px] pl-8'>
            <Typography className='font-medium mb-2 text-[24px]'>{__('Transaction Summary')}</Typography>
            <Typography className='text-gray-600 mb-6 text-[15px]'>
              {__('Payment summary - Final check before confirmation.')}
            </Typography>

            <div className='bg-[#2F2B3D0F] p-6 rounded-lg'>
              <div className='pb-4'>
                <Typography variant='body2' className='text-gray-600'>
                  {__('Total: 2 - 3 + 4')}
                </Typography>
                <Typography className='font-medium text-[#000] pt-4 text-[46px]'>
                  {__cc(data?.transfer_amount)} ₫
                </Typography>
              </div>

              <Typography variant='body2' className='text-gray-600'>
                {__('Eligible Amount Remaining')} :{' '}
                {__cc(Math.max(Number(data?.eligibility_amount) - Number(data?.request_amount), 0))} ₫
              </Typography>

              <Divider className='my-4' />

              <div>
                <Typography variant='body2' className='text-gray-600'>
                  {__('Daily Wage')} : {__cc(data?.daily_wage)} ₫
                </Typography>
                <Typography variant='body2' className='text-gray-600 my-2'>
                  {__('Assumption Working Rate')} : {__cc(data?.working_rate)} %
                </Typography>
                <Typography variant='body2' className='text-gray-600'>
                  {__('Daily Wage After Deduction')} :{' '}
                  {__n((Number(data?.daily_wage) * Number(data?.working_rate)) / 100 )} ₫
                </Typography>
              </div>
            </div>
          </div>

        </div>
      </Card>
      <TransactionEmployee
        open={openAttendanceModal}
        onClose={() => setOpenAttendanceModal(false)}
        employee={data?.user}
        data={dataEmployee?.data?.data}
        dataCompany={dataCompany?.data}
      />

      <AppLogs
        open={isOpenLogs}
        setOpen={setIsOpenLogs}
        title={__('History Menu Transaction')}
        object_type={ActivityLogTypeEnum.Transaction}
        objectId={id || ''}
      />
    </BoxPage>
  )
}

export default TransactionEmployeeDetail
